import { ProtectorType } from 'core/constants';

import { UuidableName } from 'utils/api/types';

/** A resolved record after every autosave response, but a bare uuid in between — the store writes
 * the normalised answer optimistically. */
export type LookupAnswerEntry = UuidableName | string;

// Records seen resolved, so a name survives the window where the answer is a bare uuid. Resolving
// one over HTTP instead would fan out a request per record on every consumer of the answer.
const knownRecords = new Map<string, UuidableName>();

export const rememberLookupRecords = (records: readonly (UuidableName | undefined)[]): void => {
  records.forEach((record) => {
    if (record?.uuid && record.name) {
      knownRecords.set(record.uuid, record);
    }
  });
};

export const lookupEntryUuid = (entry: LookupAnswerEntry): string => (typeof entry === 'string' ? entry : entry?.uuid);

export const lookupEntryUuids = (entries: readonly LookupAnswerEntry[]): string[] =>
  (entries || []).map(lookupEntryUuid).filter(Boolean);

export const resolveLookupEntries = (entries: readonly LookupAnswerEntry[]): UuidableName[] => {
  rememberLookupRecords((entries || []).filter((entry): entry is UuidableName => typeof entry !== 'string'));

  return lookupEntryUuids(entries).map((uuid) => knownRecords.get(uuid) || { uuid, name: uuid });
};

/** A question's lookup answer as a list; any other type's answer names no records. */
export const lookupAnswerEntries = (questionType: string | undefined, answer: unknown): LookupAnswerEntry[] => {
  if (questionType !== ProtectorType.Lookup || !answer) {
    return [];
  }

  return (Array.isArray(answer) ? answer : [answer]) as LookupAnswerEntry[];
};
